import type { NotificationCategory, NotificationIcon } from "@loot-os/api-contract";
import approvalIcon from "@loot-os/icons/icons/approval";
import archiveIcon from "@loot-os/icons/icons/archive";
import battlesIcon from "@loot-os/icons/icons/battles";
import bellIcon from "@loot-os/icons/icons/bell";
import blockIcon from "@loot-os/icons/icons/block";
import boxOpenIcon from "@loot-os/icons/icons/box-open";
import calendarIcon from "@loot-os/icons/icons/calendar";
import clockIcon from "@loot-os/icons/icons/clock";
import coinIcon from "@loot-os/icons/icons/coin";
import creditCardIcon from "@loot-os/icons/icons/credit-card";
import crownIcon from "@loot-os/icons/icons/crown";
import customPackIcon from "@loot-os/icons/icons/custom-pack";
import flameIcon from "@loot-os/icons/icons/flame";
import forgeIcon from "@loot-os/icons/icons/forge";
import giftIcon from "@loot-os/icons/icons/gift";
import heartIcon from "@loot-os/icons/icons/heart";
import idCardIcon from "@loot-os/icons/icons/id-card";
import lightningIcon from "@loot-os/icons/icons/lightning";
import megaphoneIcon from "@loot-os/icons/icons/megaphone";
import partyIcon from "@loot-os/icons/icons/party";
import percentIcon from "@loot-os/icons/icons/percent";
import priceTagIcon from "@loot-os/icons/icons/price-tag";
import rocketIcon from "@loot-os/icons/icons/rocket";
import shippingIcon from "@loot-os/icons/icons/shipping";
import sparkleIcon from "@loot-os/icons/icons/sparkle";
import starIcon from "@loot-os/icons/icons/star";
import trophyIcon from "@loot-os/icons/icons/trophy";
import tvIcon from "@loot-os/icons/icons/tv";
import unlockIcon from "@loot-os/icons/icons/unlock";
import walletIcon from "@loot-os/icons/icons/wallet";

import type { IconData } from "@loot-os/icons/types";

/**
 * How a shopper notification looks, apart from its words: the Lucide icon for each stored icon
 * name, each category's tint, its age and the bell's badge. The same rules as
 * `@loot-os/ui/lib/notifications`, which the dashboard's preview still reads until it moves here.
 */

/** The Lucide icon for each stored icon name; a template stores the name, never markup. */
export const NOTIFICATION_ICONS: Record<NotificationIcon, IconData> = {
  bell: bellIcon,
  truck: shippingIcon,
  package: boxOpenIcon,
  "package-x": blockIcon,
  trophy: trophyIcon,
  swords: battlesIcon,
  hammer: forgeIcon,
  "package-plus": customPackIcon,
  archive: archiveIcon,
  wallet: walletIcon,
  "credit-card": creditCardIcon,
  gift: giftIcon,
  "badge-check": approvalIcon,
  "shield-x": blockIcon,
  "id-card": idCardIcon,
  "lock-open": unlockIcon,
  "party-popper": partyIcon,
  sparkles: sparkleIcon,
  megaphone: megaphoneIcon,
  star: starIcon,
  tag: priceTagIcon,
  flame: flameIcon,
  zap: lightningIcon,
  heart: heartIcon,
  coins: coinIcon,
  box: boxOpenIcon,
  calendar: calendarIcon,
  clock: clockIcon,
  percent: percentIcon,
  rocket: rocketIcon,
  crown: crownIcon,
  tv: tvIcon,
};

/** Each category's icon card, from the theme's chart colors so a tenant's palette tints it. */
export const NOTIFICATION_CATEGORY_TINT: Record<NotificationCategory, string> = {
  orders: "bg-chart-3/15 text-chart-3",
  battles: "bg-chart-2/15 text-chart-2",
  forge: "bg-chart-5/15 text-chart-5",
  live_breaks: "bg-chart-1/15 text-chart-1",
  balance: "bg-chart-4/15 text-chart-4",
  account: "bg-text-primary/10 text-text-primary",
  marketing: "bg-accent-subtle text-accent-text",
};

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Pinned, so a store reads the same in every browser locale.
const dayMonth = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const dayMonthYear = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** How long ago, compactly: `now`, `12m`, `3h`, `2d`, then the date (`Sep 28`). */
export function formatNotificationAge(createdAt: string | Date, now: Date): string {
  const at = new Date(createdAt);
  const elapsed = Math.max(0, now.getTime() - at.getTime());
  if (elapsed < MINUTE_MS) return "now";
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h`;
  if (elapsed < 7 * DAY_MS) return `${Math.floor(elapsed / DAY_MS)}d`;
  return (at.getFullYear() === now.getFullYear() ? dayMonth : dayMonthYear).format(at);
}

/** Splits a newest-first list into today's (the viewer's calendar day) and everything earlier. */
export function groupNotificationsByDay<T extends { createdAt: string }>(
  notifications: readonly T[],
  now: Date,
): { today: T[]; earlier: T[] } {
  const today: T[] = [];
  const earlier: T[] = [];
  for (const notification of notifications) {
    const at = new Date(notification.createdAt);
    const sameDay =
      at.getFullYear() === now.getFullYear() &&
      at.getMonth() === now.getMonth() &&
      at.getDate() === now.getDate();
    (sameDay ? today : earlier).push(notification);
  }
  return { today, earlier };
}

/** The bell's badge: nothing at zero, the count up to nine, then `9+`. */
export function formatUnreadBadge(unread: number): string | null {
  if (unread <= 0) return null;
  return unread > 9 ? "9+" : String(unread);
}
